import { ExtractComponentProp } from '@approvalmax/types';
import { Dropzone, useExtensions } from '@approvalmax/ui/src/components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { commonConstants } from 'modules/common/constants';
import { domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { useIsOcrUploadButtonVisible } from 'pages/requestForm/hooks/useIsOcrUploadButtonVisible';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { ContentSplitViewMode, contentSplitViewState, ocrIsLoadingState } from 'shared/states';

import { uploadAttachments } from '../../actions';
import { AttachmentKind } from '../../data/AttachmentKind';
import { useEditRequestForm } from '../../hooks/useEditRequestForm';
import { getRequestTransfer } from '../../selectors/requestSelectors';
import { customGetRequestTransferState } from '../../states';
import { ocrAttachmentIdState, useWrappedCheckOcrStatus } from '../SplitViewWrapper/components';
import { ocrButtonProps } from './AttachmentsSection.constants';
import { processOcrFile } from './AttachmentsSection.helpers';
import { AttachmentsSectionProps } from './AttachmentsSection.types';

/**
 * Custom hook to handle OCR (Optical Character Recognition) related functionalities.
 */
export const useOcr = (props: Pick<AttachmentsSectionProps, 'request'>) => {
    const { request } = props;

    const dispatch = useDispatch();
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const requestTransfer = useSelector((state) =>
        request.integrationType !== domain.IntegrationType.NetSuite ? getRequestTransfer(state, request) : null
    );

    const [ocrAttachmentId, setOcrAttachmentIdState] = useAtom(ocrAttachmentIdState);
    const customGetRequestTransfer = useAtomValue(customGetRequestTransferState);
    const [isLoadingOcr, setOcrIsLoading] = useAtom(ocrIsLoadingState);
    const setContentSplitView = useSetAtom(contentSplitViewState);

    /**
     * Prepares the props for the OCR upload button.
     * Changes the onClick handler to set the dropzone settings for OCR.
     */
    const [ocrDropzoneSettings, setOcrDropzoneSettings] = useState<Pick<
        ComponentProps<typeof Dropzone>,
        'multiple' | 'accept'
    > | null>(null);
    const accept = useExtensions(commonConstants.ocrAllowedFileExtensions);
    const isOcrButtonVisible = useIsOcrUploadButtonVisible(company, request);
    const extendedOcrButtonProps = useMemo(() => {
        if (!isOcrButtonVisible) return;

        return {
            ...ocrButtonProps,
            onClick: () => setOcrDropzoneSettings({ multiple: false, accept }),
        };
    }, [accept, isOcrButtonVisible]);

    const resetOcrDropzoneSettings = useCallback(() => setOcrDropzoneSettings(null), []);

    /**
     * Checks the OCR status after the request is edited.
     */
    const { checkOcrStatus } = useWrappedCheckOcrStatus(request, ocrAttachmentId || undefined);
    const { mutateAsync: editRequest } = useEditRequestForm({
        onSuccess: () => checkOcrStatus(4000),
        onError: () => setOcrIsLoading(false),
    });

    /**
     * Callback function to handle successful OCR attachment upload.
     */
    const successfulCallback = useCallback(
        (attachments: domain.RequestAttachment[]) => {
            const newRequestTransfer =
                customGetRequestTransfer && !requestTransfer ? customGetRequestTransfer() : requestTransfer;

            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToRequest,
                attachmentId: attachments[0].id,
            });
            setOcrAttachmentIdState(attachments[0].id);
            setOcrIsLoading(true);

            return editRequest({
                body: {
                    ...newRequestTransfer,
                    ocrAttachment: attachments[0].id,
                    attachments: newRequestTransfer?.attachments
                        ? [...newRequestTransfer.attachments, attachments[0].id]
                        : [attachments[0].id],
                    currency: request.companyCurrency,
                },
            });
        },
        [
            customGetRequestTransfer,
            editRequest,
            request.companyCurrency,
            requestTransfer,
            setContentSplitView,
            setOcrAttachmentIdState,
            setOcrIsLoading,
        ]
    );

    /**
     * Function to handle OCR attachment upload.
     */
    const uploadOcrAttachments = useCallback<ExtractComponentProp<typeof Dropzone, 'onUpload'>>(
        async (file) => {
            const processedFile = await processOcrFile(file.source);

            return dispatch(
                uploadAttachments({
                    files: processedFile ? [processedFile] : [],
                    attachmentKind: AttachmentKind.General,
                    successfulCallback,
                })
            );
        },
        [dispatch, successfulCallback]
    );

    return {
        uploadOcrAttachments,
        resetOcrDropzoneSettings,
        ocrButtonProps: extendedOcrButtonProps,
        ocrDropzoneSettings,
        isLoadingOcr,
        ocrAttachmentId,
    };
};
